import { Node, mergeAttributes } from '@tiptap/core';

const VALID_ROLES = new Set(['user', 'ai', 'system']);

const normalizeRole = (value) => {
  const role = String(value || '').trim().toLowerCase();
  return VALID_ROLES.has(role) ? role : 'user';
};

const normalizeTimestamp = (value) => {
  const ts = Number(value);
  return Number.isFinite(ts) && ts > 0 ? ts : null;
};

const normalizeStatusVariant = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'stage' || normalized === 'chip' ? normalized : null;
};

const normalizeProgressText = (value) => {
  const normalized = String(value || '').trim();
  return normalized || null;
};

const normalizeModelLabel = (value) => {
  const normalized = String(value || '').trim();
  return normalized || null;
};

const normalizeArtifactText = (value) => {
  const normalized = String(value || '').trim();
  return normalized || null;
};

const parseWriteChipSummary = (text = '') => {
  const firstLine = String(text || '').split('\n')[0].trim();
  if (!firstLine) return null;
  const match = firstLine.match(/^Edited\s+(.+?)\s+\+(\d+)\s+-(\d+)$/i);
  if (!match) return null;
  return {
    label: `Edited ${String(match[1] || '').trim()}`,
    added: `+${String(match[2] || '0').trim()}`,
    removed: `-${String(match[3] || '0').trim()}`
  };
};

const buildHeaderText = (attrs = {}) => {
  const role = normalizeRole(attrs.role);
  const roleLabel = role === 'ai' ? 'Ghost AI' : role === 'system' ? 'System' : 'You';
  const ts = normalizeTimestamp(attrs.timestamp);
  if (!ts) return roleLabel;
  try {
    const dt = new Date(ts);
    const dateLabel = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${roleLabel} · ${dateLabel}`;
  } catch (err) {
    return roleLabel;
  }
};

const buildActionButton = ({ action, iconClass, label }) => {
  const iconName = String(iconClass || '').replace(/^ph[\s-]?/i, '').trim();
  return [
    'button',
    {
      type: 'button',
      class: `iconButton small chatMessageActionButton`.trim(),
      'data-chat-action': String(action || '').trim(),
      'data-tooltip': String(label || '').trim(),
      'data-tooltip-placement': 'top',
      'aria-label': String(label || '').trim(),
      contenteditable: 'false'
    },
    [
      'i',
      {
        class: `icon small ${iconName}`.trim(),
        'aria-hidden': 'true'
      }
    ]
  ];
};

const buildActionRow = (role, attrs = {}) => {
  if (role === 'user') {
    return [
      'div',
      { 'data-message-actions': '', contenteditable: 'false' },
      buildActionButton({ action: 'copy', iconClass: 'copy', label: 'Copy prompt' }),
      buildActionButton({ action: 'rerun', iconClass: 'arrows-clockwise', label: 'Rerun prompt' })
    ];
  }

  if (role === 'ai') {
    const modelLabel = normalizeModelLabel(attrs?.modelLabel);
    return [
      'div',
      { 'data-message-actions': '', contenteditable: 'false' },
      buildActionButton({ action: 'copy', iconClass: 'copy', label: 'Copy response' }),
      buildActionButton({ action: 'helpful', iconClass: 'thumbs-up', label: 'Helpful response' }),
      buildActionButton({ action: 'unhelpful', iconClass: 'thumbs-down', label: 'Unhelpful response' }),
      ...(modelLabel
        ? [[
            'span',
            { 'data-message-model': '', contenteditable: 'false' },
            modelLabel
          ]]
        : [])
    ];
  }

  return null;
};

const parseTimestamp = (element) => {
  const attr = element?.getAttribute?.('data-timestamp');
  if (!attr) return null;
  const ts = Number(attr);
  return Number.isFinite(ts) && ts > 0 ? ts : null;
};

const nodeHasVisibleContent = (node) => {
  if (!node) return false;

  if (node.isText) {
    return String(node.text || '').trim().length > 0;
  }

  if (node.type?.name === 'hardBreak') return false;

  if (node.isLeaf) {
    // Keep non-text leaf nodes (images, mentions, embeds) as meaningful content.
    return true;
  }

  const children = node.content?.content || [];
  for (const child of children) {
    if (nodeHasVisibleContent(child)) return true;
  }

  return false;
};

export const isChatMessageNodeEmpty = (node) => !nodeHasVisibleContent(node);

const findChatMessageRangeFromResolvedPos = (resolvedPos) => {
  if (!resolvedPos) return null;

  for (let depth = resolvedPos.depth; depth >= 0; depth -= 1) {
    const node = resolvedPos.node(depth);
    if (node?.type?.name !== 'chatMessage') continue;

    const start = resolvedPos.before(depth);
    return {
      from: start,
      to: start + node.nodeSize,
      node
    };
  }

  return null;
};

export const getSelectedChatMessageRange = (state) => {
  const selection = state?.selection;
  if (!selection) return null;

  const fromRange = findChatMessageRangeFromResolvedPos(selection.$from);
  if (fromRange) return fromRange;

  return findChatMessageRangeFromResolvedPos(selection.$to);
};

export const deleteEmptySelectedChatMessage = (editor) => {
  const state = editor?.state;
  const view = editor?.view;
  if (!state || !view?.dispatch) return false;

  const selected = getSelectedChatMessageRange(state);
  if (!selected || !isChatMessageNodeEmpty(selected.node)) return false;

  const tr = state.tr.delete(selected.from, selected.to);
  view.dispatch(tr);
  return true;
};

export const ChatMessage = Node.create({
  name: 'chatMessage',
  group: 'block',
  content: 'block+',
  isolating: true,

  addAttributes() {
    return {
      role: {
        default: 'user',
        parseHTML: (element) => normalizeRole(element?.getAttribute?.('data-role')),
        renderHTML: (attrs) => ({
          'data-role': normalizeRole(attrs.role)
        })
      },
      timestamp: {
        default: null,
        parseHTML: (element) => parseTimestamp(element),
        renderHTML: (attrs) => {
          const ts = normalizeTimestamp(attrs.timestamp);
          return ts ? { 'data-timestamp': String(ts) } : {};
        }
      },
      statusVariant: {
        default: null,
        parseHTML: (element) => normalizeStatusVariant(element?.getAttribute?.('data-status-variant')),
        renderHTML: (attrs) => {
          const variant = normalizeStatusVariant(attrs.statusVariant);
          return variant ? { 'data-status-variant': variant } : {};
        }
      },
      progressText: {
        default: null,
        parseHTML: (element) => normalizeProgressText(element?.getAttribute?.('data-progress-text')),
        renderHTML: (attrs) => {
          const value = normalizeProgressText(attrs.progressText);
          return value ? { 'data-progress-text': value } : {};
        }
      },
      modelLabel: {
        default: null,
        parseHTML: (element) => normalizeModelLabel(element?.getAttribute?.('data-model-label')),
        renderHTML: (attrs) => {
          const value = normalizeModelLabel(attrs.modelLabel);
          return value ? { 'data-model-label': value } : {};
        }
      },
      requestKey: {
        default: null,
        parseHTML: (element) => {
          const value = String(element?.getAttribute?.('data-request-key') || '').trim();
          return value || null;
        },
        renderHTML: (attrs) => {
          const value = String(attrs?.requestKey || '').trim();
          return value ? { 'data-request-key': value } : {};
        }
      },
      artifactText: {
        default: null,
        parseHTML: (element) => normalizeArtifactText(element?.getAttribute?.('data-artifact-text')),
        renderHTML: () => ({})
      },
      chunk: {
        default: null,
        parseHTML: (element) => {
          const value = String(element?.getAttribute?.('data-chunk') || '').trim();
          return value || null;
        },
        renderHTML: (attrs) => {
          const value = String(attrs?.chunk || '').trim();
          return value ? { 'data-chunk': value } : {};
        }
      }
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-message]' }];
  },

  addKeyboardShortcuts() {
    return {
      Delete: () => deleteEmptySelectedChatMessage(this.editor),
      Backspace: () => deleteEmptySelectedChatMessage(this.editor)
    };
  },

  renderHTML({ HTMLAttributes, node }) {
    const normalizedHtmlAttrs = HTMLAttributes && typeof HTMLAttributes === 'object'
      ? HTMLAttributes
      : {};
    const chunkAttr = String(normalizedHtmlAttrs['data-chunk'] || '').trim();
    const attrsWithoutChunk = Object.fromEntries(
      Object.entries(normalizedHtmlAttrs).filter(([key]) => key !== 'data-chunk')
    );
    const attrs = chunkAttr
      ? {
          'data-chunk': chunkAttr,
          'data-message': '',
          ...attrsWithoutChunk
        }
      : {
          'data-message': '',
          ...attrsWithoutChunk
        };

    const header = [
      'div',
      {
        'data-message-meta': '',
        contenteditable: 'false'
      },
      buildHeaderText(node?.attrs || {})
    ];

    const progressText = normalizeProgressText(node?.attrs?.progressText);
    const progress = progressText
      ? [
          'div',
          {
            'data-message-progress': '',
            contenteditable: 'false'
          },
          progressText
        ]
      : null;

    const role = normalizeRole(node?.attrs?.role);
    const statusVariant = normalizeStatusVariant(node?.attrs?.statusVariant);
    const chipSummary = role === 'ai' && statusVariant === 'chip'
      ? parseWriteChipSummary(node?.textContent || '')
      : null;
    const body = chipSummary
      ? [
          'div',
          {
            'data-message-body': '',
            'data-chat-action': 'focus-suggestion',
            role: 'button',
            tabindex: '0',
            'aria-label': 'Focus suggestion in document'
          },
          ['span', { 'data-chip-label': '' }, chipSummary.label],
          ['span', { 'data-chip-add': '' }, chipSummary.added],
          ['span', { 'data-chip-remove': '' }, chipSummary.removed]
        ]
      : ['div', { 'data-message-body': '' }, 0];
    const artifactText = role === 'ai' ? normalizeArtifactText(node?.attrs?.artifactText) : null;
    const artifact = artifactText
      ? [
          'div',
          {
            'data-message-artifact': '',
            contenteditable: 'false'
          },
          ['pre', { 'data-message-artifact-body': '' }, artifactText]
        ]
      : null;
    const isWorkingStage = role === 'ai' && statusVariant === 'stage';
    const actions = isWorkingStage ? null : buildActionRow(role, node?.attrs || {});

    if (progress && actions) {
      return artifact
        ? ['div', mergeAttributes(attrs), header, progress, body, artifact, actions]
        : ['div', mergeAttributes(attrs), header, progress, body, actions];
    }
    if (progress) {
      return artifact
        ? ['div', mergeAttributes(attrs), header, progress, body, artifact]
        : ['div', mergeAttributes(attrs), header, progress, body];
    }
    if (actions) {
      return artifact
        ? ['div', mergeAttributes(attrs), header, body, artifact, actions]
        : ['div', mergeAttributes(attrs), header, body, actions];
    }
    return artifact
      ? ['div', mergeAttributes(attrs), header, body, artifact]
      : ['div', mergeAttributes(attrs), header, body];
  }
});

export default ChatMessage;

